// Operator CFDI queue — the fiscal invoices the platform still owes.
//
// Reads GET /admin/cfdi/queue. Cross-tenant on purpose: the clinic sees its own
// state on its own billing page, and what nobody had was the view across every
// client at once.

import { apiClient } from '../../api/client.js'

export interface CfdiQueueItem {
  invoiceId: string
  tenantId: string
  tenantName: string
  /** Stripe's folio, which is what the client's accountant refers to. */
  folio: string | null
  amountTotalMxn: number
  currency: string
  paidAt: string | null
  status: string
  /** The PAC's own message. Operator-facing here, unlike on the clinic side. */
  reason: string | null
  attempts: number
  lastAttemptAt: string | null
  hasFiscalData: boolean
  /** The sweep has given up; only an explicit retry moves this one. */
  exhausted: boolean
  /** Whose move it is — the first question anyone reading this list asks. */
  blockedOn: 'nosotros' | 'el cliente'
}

export interface CfdiQueue {
  maxAttempts: number
  /** How many exist, not how many were returned — the list caps at 200. */
  total: number
  items: CfdiQueueItem[]
}

/**
 * GET /admin/cfdi/queue — cross-tenant list of paid invoices still owing a
 * CFDI. `total` counts all; `items` caps at 200.
 */
export async function getCfdiQueue(): Promise<CfdiQueue> {
  const res = await apiClient.get<{ data: CfdiQueue }>('/admin/cfdi/queue')
  return res.data.data
}

/**
 * Put an invoice back in front of the issuance sweep.
 *
 * It does not stamp here and it does not stamp on the server either: the retry
 * resets the attempt counter, and the sweep does the work through the same
 * claim and folio reconciliation as every other issuance.
 */
export async function retryCfdi(invoiceId: string): Promise<void> {
  await apiClient.post(`/admin/cfdi/${encodeURIComponent(invoiceId)}/retry`, {})
}

// ── Per-tenant CFDI chain (TenantDetail → Facturación) ──────────────────────

export interface TenantCfdiDocument {
  uuid: string
  folio: string | null
  status: string
  issuedAt: string | null
  cancelMotivo: string | null
  replacesUuid: string | null
  cancelRequestedAt: string | null
  cancelledAt: string | null
}

export interface TenantCfdiInvoice {
  invoiceId: string
  folio: string | null
  amountTotalMxn: number
  currency: string
  invoiceStatus: string
  paidAt: string | null
  cfdiStatus: string | null
  /** The document the invoice currently points at. */
  liveUuid: string | null
  /** Every document ever stamped for this invoice — a substitution shows as
   *  two, which is exactly what this list exists to make visible. */
  documents: TenantCfdiDocument[]
}

/**
 * GET /admin/tenants/:id/cfdis — one tenant's invoices with the full document
 * chain per invoice (substitutions appear as two documents on purpose).
 */
export async function getTenantCfdis(tenantId: string): Promise<TenantCfdiInvoice[]> {
  const res = await apiClient.get<{ data: { items: TenantCfdiInvoice[] } }>(
    `/admin/tenants/${encodeURIComponent(tenantId)}/cfdis`,
  )
  return res.data.data.items
}

/** The mid-call handover: the Stripe hosted-invoice URL the salesperson
 *  pastes into the chat so the client pays before the call ends. */
export async function getInvoicePayLink(tenantId: string, invoiceId: string): Promise<string> {
  const res = await apiClient.post<{ data: { url: string } }>(
    `/admin/tenants/${encodeURIComponent(tenantId)}/invoices/${encodeURIComponent(invoiceId)}/pay-link`,
  )
  return res.data.data.url
}

/** Direct cancellation (motivos 02/03). 01 goes through substituteCfdiRequest,
 *  which supplies the replacement — the SAT's required order. */
export async function cancelCfdi(
  invoiceId: string,
  motivo: '02' | '03',
  uuid: string,
): Promise<{ status: string }> {
  const res = await apiClient.post<{ data: { status: string } }>(
    `/admin/cfdi/${encodeURIComponent(invoiceId)}/cancel`,
    // The uuid pins the DOCUMENT the operator was looking at: if a racing
    // substitution changed what is live, the server refuses instead of acting
    // on a document nobody clicked.
    { motivo, uuid },
  )
  return res.data.data
}

/** Motivo 01: re-stamp with the tenant's CURRENT fiscal data, then cancel the
 *  original citing the replacement. */
export async function substituteCfdiRequest(
  invoiceId: string,
  uuid: string,
): Promise<{ replacementUuid: string; cancellation: string }> {
  const res = await apiClient.post<{ data: { replacementUuid: string; cancellation: string } }>(
    `/admin/cfdi/${encodeURIComponent(invoiceId)}/substitute`,
    { uuid },
  )
  return res.data.data
}
